import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarLayout from '../../components/SidebarLayout';
import AddSchoolModal from '../../components/AddSchoolModal';
import UserSearchDropdown from '../../components/UserSearchDropdown';
import ViewDataContent from '../../components/ViewDataContent';
import SchemaAddDataPanel from '../../components/schemas/SchemaAddDataPanel.jsx';
import SchemaEditTableModal from '../../components/schemas/SchemaEditTableModal.jsx';
import SchemaCreateTablePanel from '../../components/schemas/SchemaCreateTablePanel.jsx';
import './styles.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


export const AdminSchoolsPanel = ({ embedded = false }) => {
  const [entities, setEntities] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal states
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityUsers, setEntityUsers] = useState([]);
  const [entitySchemas, setEntitySchemas] = useState([]);
  const [entityDetailsLoading, setEntityDetailsLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [activeSchemaAction, setActiveSchemaAction] = useState(null); // 'add' | 'view' | 'edit' | 'create'
  const [actionSchema, setActionSchema] = useState(null);
  const [updatingSchemaId, setUpdatingSchemaId] = useState(null);
  const [schemaStatusModalOpen, setSchemaStatusModalOpen] = useState(false);
  const [schemaToToggle, setSchemaToToggle] = useState(null);
  const [schemaNextActive, setSchemaNextActive] = useState(true);
  const [schemaStatusError, setSchemaStatusError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const usersPanelRef = useRef(null);
  const templatesPanelRef = useRef(null);
  const actionsPanelRef = useRef(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchEntities();
  }, []);

  // Reset to first page when filters or data change
  useEffect(() => {
    setCurrentPage(1);
  }, [query, entities.length]);

  const fetchEntities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/entities`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch entities');
      }
      const data = await response.json();
      setEntities(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEntityAdded = (newEntity) => {
    setEntities((prev) =>
      [...prev, newEntity].sort((a, b) => a.entity_name.localeCompare(b.entity_name))
    );
  };

  const openDeleteEntityModal = (entity) => {
    setEntityToDelete(entity);
    setDeleteConfirm('');
    setDeleteError('');
    setDeleteModalOpen(true);
  };

  const closeDeleteEntityModal = () => {
    if (deleting) return;
    setDeleteModalOpen(false);
    setEntityToDelete(null);
    setDeleteConfirm('');
    setDeleteError('');
  };

  const handleDeleteEntity = async () => {
    if (!entityToDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/entities/${entityToDelete.entity_id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete entity');
      }

      // Remove from list and reset selections
      setEntities((prev) => prev.filter((e) => e.entity_id !== entityToDelete.entity_id));
      if (selectedEntity?.entity_id === entityToDelete.entity_id) {
        setSelectedEntity(null);
        setUsersModalOpen(false);
        setTemplatesModalOpen(false);
        setActiveSchemaAction(null);
        setActionSchema(null);
      }
      closeDeleteEntityModal();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete entity');
    } finally {
      setDeleting(false);
    }
  };

  const fetchEntityDetails = async (entityId) => {
    setEntityDetailsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/entities/${entityId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch entity details');
      }

      const data = await response.json();
      setEntityUsers(data.data.users || []);
      setEntitySchemas(data.data.schemas || []);
    } catch (err) {
      console.error('Error fetching entity details:', err);
      setEntityUsers([]);
      setEntitySchemas([]);
    } finally {
      setEntityDetailsLoading(false);
    }
  };

  const handleOpenUsersModal = async (entity) => {
    setSelectedEntity(entity);
    setTemplatesModalOpen(false);
    setUsersModalOpen(true);
    setShowAddUser(false);
    await fetchEntityDetails(entity.entity_id);
  };

  const handleOpenTemplatesModal = async (entity) => {
    setSelectedEntity(entity);
    setUsersModalOpen(false);
    setShowAddUser(false);
    setActiveSchemaAction(null);
    setActionSchema(null);
    setTemplatesModalOpen(true);
    await fetchEntityDetails(entity.entity_id);
  };

  const handleUserSelect = async (selectedUser) => {
    if (!selectedEntity) return;
    setAddingUser(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/entities/${selectedEntity.entity_id}/add-user`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: selectedUser.user_id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add user');
      }

      setShowAddUser(false);
      await fetchEntityDetails(selectedEntity.entity_id);
    } catch (err) {
      alert(err.message);
    } finally {
      setAddingUser(false);
    }
  };

  // Close inline panels / templates on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (usersModalOpen) {
          setUsersModalOpen(false);
          setShowAddUser(false);
        }
        if (templatesModalOpen) {
          setTemplatesModalOpen(false);
        }
      }
    };

    if (usersModalOpen || templatesModalOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [usersModalOpen, templatesModalOpen]);

  useEffect(() => {
    if (usersModalOpen && usersPanelRef.current) {
      usersPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [usersModalOpen, selectedEntity]);

  useEffect(() => {
    if (templatesModalOpen && templatesPanelRef.current) {
      templatesPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [templatesModalOpen, selectedEntity]);

  useEffect(() => {
    if (templatesModalOpen && actionsPanelRef.current && (actionSchema || activeSchemaAction === 'create')) {
      actionsPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [templatesModalOpen, actionSchema, activeSchemaAction]);

  const handleSchemaActionClick = (schema, action) => {
    setActionSchema(schema);
    setActiveSchemaAction(action); // 'add' | 'view' | 'edit'
    // Scroll is handled by useEffect when activeSchemaAction changes
  };

  const openSchemaStatusModal = (schema) => {
    const currentlyActive = schema.is_active !== false;
    setSchemaToToggle(schema);
    setSchemaNextActive(!currentlyActive);
    setSchemaStatusError('');
    setSchemaStatusModalOpen(true);
  };

  const closeSchemaStatusModal = () => {
    if (updatingSchemaId) return;
    setSchemaStatusModalOpen(false);
    setSchemaToToggle(null);
    setSchemaStatusError('');
  };

  const handleToggleSchemaActive = async () => {
    if (!selectedEntity || !schemaToToggle) return;
    setUpdatingSchemaId(schemaToToggle.schema_id);
    setSchemaStatusError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/admin/entities/${selectedEntity.entity_id}/schemas/${schemaToToggle.schema_id}/status`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_active: schemaNextActive })
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update table status');
      }

      const nextActive = data.data?.is_active ?? schemaNextActive;
      setEntitySchemas((prev) =>
        prev.map((schema) =>
          schema.schema_id === schemaToToggle.schema_id
            ? { ...schema, is_active: nextActive }
            : schema
        )
      );
      if (actionSchema?.schema_id === schemaToToggle.schema_id) {
        setActionSchema((prev) => (prev ? { ...prev, is_active: nextActive } : prev));
        if (!nextActive && (activeSchemaAction === 'add' || activeSchemaAction === 'edit')) {
          setActiveSchemaAction(null);
          setActionSchema(null);
        }
      }
      setSchemaStatusModalOpen(false);
      setSchemaToToggle(null);
    } catch (err) {
      setSchemaStatusError(err.message || 'Failed to update table status');
    } finally {
      setUpdatingSchemaId(null);
    }
  };

  const PAGE_SIZE = 5;

  const filteredEntities = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entities;
    return entities.filter((entity) =>
      (entity.entity_name || '').toLowerCase().includes(q)
    );
  }, [entities, query]);
  const totalPages = Math.max(1, Math.ceil((filteredEntities.length || 1) / PAGE_SIZE));

  const paginatedEntities = useMemo(() => {
    const safePage = Math.min(Math.max(currentPage, 1), totalPages);
    const startIndex = (safePage - 1) * PAGE_SIZE;
    return filteredEntities.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredEntities, currentPage, totalPages]);

  const totalEntities = entities.length;
  const skeletonCards = Array.from({ length: PAGE_SIZE });

  return (
    <div
      className={`admin-schools-container${embedded ? ' admin-schools-container--embedded' : ''
        }`}
    >
      <div className="admin-schools-header">
        <div>
          <h1>Entities Management</h1>
          <small>Manage entities and user assignments</small>
        </div>
        <div className="admin-schools-header-actions">
          <div className="admin-total-pill">
            Total entities: <strong>{totalEntities}</strong>
          </div>
          <button className="admin-add-school-btn" onClick={() => setShowAddModal(true)}>
            + Add Entity
          </button>
        </div>
      </div>

      <div className="admin-search-card">
        <input
          className="admin-dashboard-search"
          type="text"
          placeholder="Search entities by name..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-schools-table">
        {!loading && !error && filteredEntities.length > 0 && (
          <div className="admin-table-toolbar">
            <div className="admin-table-toolbar-info">
              {(() => {
                const safePage = Math.min(Math.max(currentPage, 1), totalPages);
                const start = (safePage - 1) * PAGE_SIZE + 1;
                const end = Math.min(safePage * PAGE_SIZE, filteredEntities.length);
                return (
                  <span>
                    Showing <strong>{start}</strong>–<strong>{end}</strong> of{' '}
                    <strong>{filteredEntities.length}</strong> entities
                  </span>
                );
              })()}
            </div>
            <div className="admin-table-toolbar-pagination">
              <button
                className="admin-pagination-btn"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="admin-pagination-page">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </span>
              <button
                className="admin-pagination-btn"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {!loading && !error && filteredEntities.length === 0 ? (
          <div className="admin-empty-state admin-empty-state--flush">
            <p>{entities.length === 0 ? 'No entities found.' : 'No entities matched your search.'}</p>
            <span>
              {entities.length === 0
                ? 'Use "Add Entity" to onboard your first organization.'
                : 'Try adjusting the search query above or use "Add Entity" to create a new one.'}
            </span>
          </div>
        ) : (
          <table className="admin-schools-grid">
            <thead>
              <tr>
                <th scope="col">Entity Name</th>
                <th scope="col">Entity ID</th>
                <th scope="col">Created Date</th>
                <th scope="col">Is_Org_Entity</th>
                <th scope="col" className="actions-col">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? skeletonCards.map((_, idx) => (
                  <tr key={`row-skeleton-${idx}`} className="skeleton-row">
                    <td>
                      <span className="skeleton-line" />
                    </td>
                    <td>
                      <span className="skeleton-line" />
                    </td>
                    <td>
                      <span className="skeleton-line" />
                    </td>
                    <td>
                      <span className="skeleton-line" />
                    </td>
                    <td>
                      <span className="skeleton-pill" />
                    </td>
                  </tr>
                ))
                : paginatedEntities.map((entity, idx) => (
                  <tr key={entity.entity_id || idx}>
                    <td className="cell-strong">
                      {entity.entity_name || 'Unnamed Entity'}
                    </td>
                    <td className="cell-mono">{entity.entity_id || 'Unknown'}</td>
                    <td className="cell-muted">
                      {entity.created_at
                        ? new Date(entity.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : 'N/A'}
                    </td>
                    <td>
                      {entity.is_org_entity ? (
                        <span className="admin-org-badge admin-org-badge--yes">Yes</span>
                      ) : (
                        <span className="admin-org-badge admin-org-badge--no">No</span>
                      )}
                    </td>
                    <td className="actions-col">
                      <div className="admin-table-actions">
                        <button
                          onClick={() => handleOpenUsersModal(entity)}
                          className="admin-users-btn"
                        >
                          Users
                        </button>
                        <button
                          onClick={() => handleOpenTemplatesModal(entity)}
                          className="admin-templates-btn"
                        >
                          Templates
                        </button>
                        <button
                          onClick={() => openDeleteEntityModal(entity)}
                          className="admin-delete-entity-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <AddSchoolModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onEntityAdded={handleEntityAdded}
      />

      {/* Users inline section */}
      {usersModalOpen && selectedEntity && (
        <div className="admin-users-inline-panel" ref={usersPanelRef}>
          <div className="admin-users-inline-header">
            <div>
              <h2>{selectedEntity.entity_name} - Users</h2>
              <p className="admin-users-inline-subtitle">
                Manage users assigned to this entity.
              </p>
            </div>
            <button
              className="admin-users-inline-close"
              onClick={() => {
                setUsersModalOpen(false);
                setShowAddUser(false);
              }}
              aria-label="Close users panel"
            >
              ✕
            </button>
          </div>
          <div className="admin-users-inline-body">
            <div className="admin-section">
              <div className="admin-section-header">
                <h3>Users ({entityUsers.length})</h3>
                <button
                  onClick={() => setShowAddUser(!showAddUser)}
                  className="admin-add-btn"
                >
                  Add User
                </button>
              </div>

              {showAddUser && (
                <div className="admin-add-user-form">
                  <div className="admin-user-search-container">
                    <UserSearchDropdown
                      onUserSelect={handleUserSelect}
                      placeholder="Search for a user to add..."
                      excludeUserIds={entityUsers.map((user) => user.user_id)}
                    />
                  </div>
                  <div className="admin-add-user-actions">
                    <button
                      onClick={() => setShowAddUser(false)}
                      className="admin-cancel-btn"
                      disabled={addingUser}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {entityDetailsLoading ? (
                <div className="admin-table admin-users-table">
                  <div className="admin-table-header">
                    <span>Name</span>
                    <span>Username</span>
                    <span>Email</span>
                    <span>Role</span>
                  </div>
                  <div className="admin-table-body">
                    {Array.from({ length: 5 }).map((_, idx) => (
                      <div key={`user-skeleton-${idx}`} className="admin-table-row skeleton-row">
                        <span><span className="skeleton-line" /></span>
                        <span><span className="skeleton-line" /></span>
                        <span><span className="skeleton-line" /></span>
                        <span><span className="skeleton-line" /></span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : entityUsers.length === 0 ? (
                <div className="admin-empty-state">No users assigned to this entity</div>
              ) : (
                <div className="admin-table admin-users-table">
                  <div className="admin-table-header">
                    <span>Name</span>
                    <span>Username</span>
                    <span>Email</span>
                    <span>Role</span>
                  </div>
                  <div className="admin-table-body">
                    {entityUsers.map((user) => (
                      <div key={user.user_id} className="admin-table-row">
                        <span className="cell-strong">{user.full_name || user.username}</span>
                        <span>@{user.username || 'unknown'}</span>
                        <span>{user.email || '—'}</span>
                        <span>{user.role || 'Member'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Templates inline section */}
      {templatesModalOpen && selectedEntity && (
        <div className="admin-templates-inline-panel" ref={templatesPanelRef}>
          <div className="admin-templates-inline-header">
            <div>
              <h2>{selectedEntity.entity_name} - Custom Templates</h2>
              <p className="admin-templates-inline-subtitle">
                Manage data schemas and custom templates for this entity.
              </p>
            </div>
            <button
              className="admin-templates-inline-close"
              onClick={() => setTemplatesModalOpen(false)}
              aria-label="Close templates panel"
            >
              ✕
            </button>
          </div>
          <div className="admin-templates-inline-body">
            <div className="admin-section">
              <div className="admin-section-header">
                <h3>Data Schemas ({entitySchemas.length})</h3>
                <div className="admin-schema-actions">
                  <button
                    onClick={() => {
                      setActionSchema(null);
                      setActiveSchemaAction('create');
                    }}
                    className="admin-create-table-btn"
                  >
                    + Create New Table
                  </button>
                </div>
              </div>

              {entityDetailsLoading ? (
                <div className="admin-table admin-schemas-table">
                  <div className="admin-table-header">
                    <span>Table</span>
                    {/* <span>Version</span> */}
                    <span>Created</span>
                    <span>Columns</span>
                    <span>Status</span>
                    <span className="actions-col">Actions</span>
                  </div>
                  <div className="admin-table-body">
                    {Array.from({ length: 5 }).map((_, idx) => (
                      <div key={`schema-skeleton-${idx}`} className="admin-table-row skeleton-row">
                        <span><span className="skeleton-line" /></span>
                        {/* <span><span className="skeleton-line" /></span> */}
                        <span><span className="skeleton-line" /></span>
                        <span><span className="skeleton-line" /></span>
                        <span><span className="skeleton-line" /></span>
                        <span className="actions-col"><span className="skeleton-pill" /></span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : entitySchemas.length === 0 ? (
                <div className="admin-empty-state">No schemas defined for this entity</div>
              ) : (
                <div className="admin-table admin-schemas-table">
                  <div className="admin-table-header">
                    <span>Table</span>
                    {/* <span>Version</span> */}
                    <span>Created</span>
                    <span>Columns</span>
                    <span>Status</span>
                    <span className="actions-col">Actions</span>
                  </div>
                  <div className="admin-table-body">
                    {entitySchemas.map((schema) => {
                      const columnCount = schema.schema_definition?.columns?.length ?? 0;
                      const isActive = schema.is_active !== false;
                      const isUpdating = updatingSchemaId === schema.schema_id;
                      return (
                        <div
                          key={schema.schema_id}
                          className={`admin-table-row${isActive ? '' : ' is-inactive-schema'}`}
                        >
                          <span className="cell-strong">{schema.table_name}</span>
                          {/* <span>{schema.version || '—'}</span> */}
                          <span>{new Date(schema.created_at).toLocaleDateString()}</span>
                          <span>{columnCount}</span>
                          <span>
                            <span className={`admin-schema-status ${isActive ? 'is-active' : 'is-inactive'}`}>
                              {isActive ? 'Active' : 'Inactive'}
                            </span>
                          </span>
                          <span className="actions-col table-actions">
                            <button
                              onClick={() => handleSchemaActionClick(schema, 'add')}
                              className="admin-schema-action-btn admin-add-data-btn"
                              disabled={!isActive}
                            >
                              Add Data
                            </button>
                            <button
                              onClick={() => handleSchemaActionClick(schema, 'view')}
                              className="admin-schema-action-btn admin-view-data-btn"
                            >
                              View Data
                            </button>
                            <button
                              onClick={() => handleSchemaActionClick(schema, 'edit')}
                              className="admin-schema-action-btn admin-edit-table-btn"
                              disabled={!isActive}
                            >
                              Edit Table
                            </button>
                            <button
                              onClick={() => openSchemaStatusModal(schema)}
                              className={`admin-schema-action-btn ${isActive ? 'admin-deactivate-table-btn' : 'admin-activate-table-btn'}`}
                              disabled={isUpdating}
                            >
                              {isUpdating
                                ? (isActive ? 'Deactivating...' : 'Activating...')
                                : (isActive ? 'Deactivate' : 'Activate')}
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions inline section (for selected schema or create action) */}
      {templatesModalOpen && selectedEntity && (actionSchema || activeSchemaAction === 'create') && (
        <div className="admin-actions-inline-panel" ref={actionsPanelRef}>
          <div className="admin-actions-inline-header">
            <div>
              <h2>
                {activeSchemaAction === 'create' 
                  ? 'Create New Table' 
                  : `Schema Actions - ${actionSchema?.table_name}`}
              </h2>
              <p className="admin-actions-inline-subtitle">
                {activeSchemaAction === 'create'
                  ? 'Define the structure for the entity\'s data table'
                  : 'Quick actions for the selected standard template.'}
              </p>
            </div>
            <button
              className="admin-actions-inline-close"
              onClick={() => {
                setActiveSchemaAction(null);
                setActionSchema(null);
              }}
              aria-label="Close actions panel"
            >
              ✕
            </button>
          </div>
          <div className="admin-actions-inline-body">
            {activeSchemaAction !== 'create' && (
              <div className="admin-actions-inline-meta">
                <span className="admin-actions-chip">
                  {activeSchemaAction === 'add' && 'Add Data'}
                  {activeSchemaAction === 'view' && 'View Data'}
                  {activeSchemaAction === 'edit' && 'Edit Table'}
                  {!activeSchemaAction && 'Select an action'}
                </span>
                <span className="admin-actions-meta-text">
                  Created on{' '}
                  {actionSchema?.created_at
                    ? new Date(actionSchema.created_at).toLocaleDateString()
                    : 'N/A'}{' '}
                  • {actionSchema?.schema_definition?.columns?.length ?? 0} columns
                </span>
              </div>
            )}
            {/* <div className="admin-actions-inline-buttons">
              <button
                className={`admin-schema-action-btn admin-add-data-btn ${activeSchemaAction === 'add' ? 'is-primary' : ''}`}
                onClick={() =>
                  setActiveSchemaAction((prev) => (prev === 'add' ? null : 'add'))
                }
              >
                Add Data
              </button>
              <button
                className={`admin-schema-action-btn admin-view-data-btn ${activeSchemaAction === 'view' ? 'is-primary' : ''}`}
                onClick={() =>
                  setActiveSchemaAction((prev) => (prev === 'view' ? null : 'view'))
                }
              >
                View Data
              </button>
              <button
                className={`admin-schema-action-btn admin-edit-table-btn ${activeSchemaAction === 'edit' ? 'is-primary' : ''}`}
                onClick={() =>
                  setActiveSchemaAction((prev) => (prev === 'edit' ? null : 'edit'))
                }
              >
                Edit Table
              </button>
            </div> */}

            {activeSchemaAction === 'add' && (
              <div className="admin-actions-inline-content">
                <SchemaAddDataPanel
                  entityId={selectedEntity.entity_id}
                  schemaId={actionSchema.schema_id}
                  onClose={() => {
                    setActiveSchemaAction(null);
                    setActionSchema(null);
                  }}
                  onSuccess={async () => {
                    await fetchEntityDetails(selectedEntity.entity_id);
                  }}
                />
              </div>
            )}

            {activeSchemaAction === 'view' && (
              <div className="admin-actions-inline-content">
                <ViewDataContent
                  schemaId={actionSchema.schema_id}
                  isInline
                  adminMode
                  entityId={selectedEntity.entity_id}
                />
              </div>
            )}

            {activeSchemaAction === 'edit' && (
              <div className="admin-actions-inline-content">
              <SchemaEditTableModal
                entityId={selectedEntity.entity_id}
                schemaId={actionSchema.schema_id}
                inline
                onClose={() => {
                  setActiveSchemaAction(null);
                  setActionSchema(null);
                }}
                onSuccess={async () => {
                  await fetchEntityDetails(selectedEntity.entity_id);
                }}
              />
              </div>
            )}

            {activeSchemaAction === 'create' && (
              <div className="admin-actions-inline-content">
                <SchemaCreateTablePanel
                  entityId={selectedEntity.entity_id}
                  onClose={() => {
                    setActiveSchemaAction(null);
                    setActionSchema(null);
                  }}
                  onSuccess={async () => {
                    await fetchEntityDetails(selectedEntity.entity_id);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activate / deactivate schema confirmation modal */}
      {schemaStatusModalOpen && schemaToToggle && (
        <div className="modal-overlay" onClick={closeSchemaStatusModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {schemaNextActive ? 'Activate' : 'Deactivate'} table "{schemaToToggle.table_name}"
              </h3>
            </div>
            <div className="modal-body">
              <p className="modal-text">
                {schemaNextActive
                  ? 'This table will become visible again to users of this entity.'
                  : 'This table will be hidden from entity users until it is activated again. Existing data is kept.'}
              </p>
              {schemaStatusError && <div className="admin-delete-error">{schemaStatusError}</div>}
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={closeSchemaStatusModal}
                disabled={!!updatingSchemaId}
              >
                Cancel
              </button>
              <button
                className={schemaNextActive ? 'confirm-activate-btn' : 'danger-btn'}
                onClick={handleToggleSchemaActive}
                disabled={!!updatingSchemaId}
              >
                {updatingSchemaId
                  ? (schemaNextActive ? 'Activating...' : 'Deactivating...')
                  : (schemaNextActive ? 'Activate' : 'Deactivate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete entity confirmation modal */}
      {deleteModalOpen && entityToDelete && (
        <div className="modal-overlay" onClick={closeDeleteEntityModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete entity "{entityToDelete.entity_name}"</h3>
            </div>
            <div className="modal-body">
              <p className="modal-text">
                This action cannot be undone. This will permanently delete the entity and all associated data.
              </p>
              <p className="modal-subtext">
                Please type <strong style={{ color: '#000', fontWeight: 'bold' }}>Delete {entityToDelete.entity_name}</strong> to confirm.
              </p>
              <input
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={`Type Delete ${entityToDelete.entity_name} to confirm`}
                disabled={deleting}
                className={`confirm-input ${
                  deleteConfirm === `Delete ${entityToDelete.entity_name}` ? 'valid' : deleteConfirm ? 'invalid' : ''
                }`}
              />
              {deleteError && <div className="admin-delete-error">{deleteError}</div>}
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={closeDeleteEntityModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="danger-btn"
                onClick={handleDeleteEntity}
                disabled={
                  deleting ||
                  deleteConfirm !== `Delete ${entityToDelete.entity_name}`
                }
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminSchools = () => (
  <SidebarLayout>
    <AdminSchoolsPanel />
  </SidebarLayout>
);

export default AdminSchools;

